import type { RowId, FieldId } from '@eformulieren/model'
import { useCallback } from 'react'
import { useSnapshot } from './snapshot.js'
import type * as Y from 'yjs'
import {
  type Origin,
  originIn,
  plainTextOfFragment,
  rowsList,
  orderedRowIds,
  valuesMap,
} from './sectionDocument.js'

/**
 * React op een sectiedocument.
 *
 * `useSnapshot` (die `useSyncExternalStore` gebruikt) en niet `useState` met een effect:
 * het document is de
 * bron en React is de weergave. Bij een externe bron die tijdens het renderen kan
 * wijzigen voorkomt deze hook dat twee delen van het scherm verschillende standen
 * laten zien.
 *
 * **De eigen historie van de editor blijft hier buiten.** Undo en redo komen van
 * de samenwerklaag en niet van de component (valkuil 5 uit ontwerp bijlage C.4):
 * twee stapels tegelijk botsen. In M2 is er nog geen samenwerklaag, dus is er ook
 * nog geen undo; die komt met M3.
 */

/** De waarde van één veld uit de waardenmap, en niets als er nog niets staat. */
export function useValue(doc: Y.Doc, fieldId: FieldId): unknown {
  const map = valuesMap(doc)
  const subscribe = useCallback(
    (redraw: () => void) => {
      const listen = (event: Y.YMapEvent<unknown>): void => {
        // Alleen hertekenen als dit veld erbij zit: een sectie kan tientallen
        // velden hebben en die horen niet allemaal mee te bewegen met één toets.
        if (event.keysChanged.has(fieldId)) redraw()
      }
      map.observe(listen)
      return () => map.unobserve(listen)
    },
    [map, fieldId],
  )
  const read = useCallback(() => map.get(fieldId), [map, fieldId])
  return useSnapshot(subscribe, read)
}

/** Wie het veld als laatste raakte en wanneer (R-97, R-188). */
export function useOrigin(doc: Y.Doc, fieldId: FieldId): Origin | undefined {
  return useOriginIn(valuesMap(doc), fieldId)
}

/**
 * Hetzelfde voor een veld in een willekeurige map: de waardenmap, of de map van een rij.
 *
 * Een cel in een herhaalgroep heeft zijn herkomst naast zijn waarde in de rijmap (6.8), en de
 * stempel op een akkoord in een rij (R-19) leest hem daar.
 */
export function useOriginIn(map: Y.Map<unknown>, fieldId: string): Origin | undefined {
  const subscribe = useCallback(
    (redraw: () => void) => {
      const listen = (event: Y.YMapEvent<unknown>): void => {
        if ([...event.keysChanged].some((key) => key.startsWith(fieldId))) redraw()
      }
      map.observe(listen)
      return () => map.unobserve(listen)
    },
    [map, fieldId],
  )
  const read = useCallback(() => originIn(map, fieldId), [map, fieldId])
  return useSnapshot(subscribe, read)
}

/**
 * De rij-id's van een herhaalgroep, in de volgorde van de lijst.
 *
 * Alleen de id's en niet de rijen zelf: een component per rij abonneert zich op
 * zijn eigen velden, zodat een wijziging in rij 3 rij 1 niet hertekent. Dat is ook
 * wat de rij-id's stabiel maakt (R-40).
 */
export function useRowIds(doc: Y.Doc, fieldId: FieldId): readonly RowId[] {
  const list = rowsList(doc, fieldId)
  const subscribe = useCallback(
    (redraw: () => void) => {
      // Diep, want een verplaatsing is een sleutel in een rijmap en geen wijziging van de lijst
      // (`moveRowTo`). Typen in een cel meldt zich hier ook; `useSnapshot` vergelijkt de id's en
      // hertekent dan niet.
      list.observeDeep(redraw)
      return () => list.unobserveDeep(redraw)
    },
    [list],
  )
  const read = useCallback(
    () =>
      orderedRowIds(list),
    [list],
  )
  return useSnapshot(subscribe, read)
}

/** Dezelfde lijst rij-id's, maar dan van een lijst die de aanroeper al heeft. */
export function useRowIdsOf(list: Y.Array<Y.Map<unknown>>): readonly RowId[] {
  const subscribe = useCallback(
    (redraw: () => void) => {
      // Diep, want een verplaatsing is een sleutel in een rijmap en geen wijziging van de lijst
      // (`moveRowTo`). Typen in een cel meldt zich hier ook; `useSnapshot` vergelijkt de id's en
      // hertekent dan niet.
      list.observeDeep(redraw)
      return () => list.unobserveDeep(redraw)
    },
    [list],
  )
  const read = useCallback(
    () =>
      orderedRowIds(list),
    [list],
  )
  return useSnapshot(subscribe, read)
}

/**
 * De waarde van één cel in één rij.
 *
 * Per cel een eigen abonnement, zodat typen in rij 3 rij 1 niet hertekent. Dat is
 * ook waarom de rij-id's stabiel moeten zijn (R-40): een rij die van identiteit
 * wisselt zou elk abonnement in de groep laten verspringen.
 */
export function useRowValue(row: Y.Map<unknown>, fieldId: FieldId): unknown {
  const subscribe = useCallback(
    (redraw: () => void) => {
      const listen = (event: Y.YMapEvent<unknown>): void => {
        if (event.keysChanged.has(fieldId)) redraw()
      }
      row.observe(listen)
      return () => row.unobserve(listen)
    },
    [row, fieldId],
  )
  const read = useCallback(() => row.get(fieldId), [row, fieldId])
  return useSnapshot(subscribe, read)
}

/**
 * De platte tekst van een rich-text-fragment, meelopend met het document.
 *
 * `observeDeep` en niet de `onUpdate` van de editor: die vuurt alleen bij een eigen
 * wijziging. Een medeschrijver die honderd woorden toevoegt zou de teller dan niet
 * laten bewegen, en dan klopt het getal onder het veld niet meer met de tekst
 * (R-12, R-13, R-72).
 */
export function usePlainText(fragment: Y.XmlFragment): string {
  const subscribe = useCallback(
    (redraw: () => void) => {
      fragment.observeDeep(redraw)
      return () => fragment.unobserveDeep(redraw)
    },
    [fragment],
  )
  // Platte tekst is een string, dus `Object.is` vergelijkt hem al goed; hij gaat langs dezelfde
  // helper zodat er één weg is en niet twee.
  const read = useCallback(() => plainTextOfFragment(fragment), [fragment])
  return useSnapshot(subscribe, read)
}
